import { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { usePublishChatContext } from '@/features/chatbot/usePublishChatContext';
import type { ChatField } from '@/features/chatbot/types';
import { useTranslation } from 'react-i18next';
import {
  FaSpinner, FaCheck, FaCopy, FaGear,
  FaCircleCheck, FaCircleExclamation, FaTerminal
} from 'react-icons/fa6';
import { TrainingApiService } from '../../features/training/services/trainingApiService';
import type {
  ApiParameter,
  SaveForecastConfigPayload
} from '../../features/training/services/trainingApiService/methods/forecastMethods';
import type { ApiKeyInfo } from '../../features/training/services/trainingApiService/methods/apiKeyMethods';
import { getApiBaseUrl } from '../../features/training/services/trainingApiService/core';
import { ConfirmDialog } from '@/shared/components/modals';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';
import UpgradeModal from '@/features/subscription/components/UpgradeModal';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FileEntry {
  id: string;
  bezeichnung: string;
  type: string;
  zeithorizont_start: number;
  zeithorizont_end: number;
  skalierung: string;
  skalierung_min: number | null;
  skalierung_max: number | null;
  datenanpassung: string | null;
  data_source: string | null;
  api_source: string | null;
  fcst_var: string | null;
  latitude: number | null;
  longitude: number | null;
  feature_index: number | null;
  storage_path: string | null;
}

interface SessionConfig {
  files: FileEntry[];
  time_info: Record<string, unknown> | null;
  zeitschritte: {
    eingabe: number;
    ausgabe: number;
    zeitschrittweite: number;
    offset: number;
  } | null;
  models: Array<{ filename: string; model_type: string; format: string; file_size_mb: number }>;
}

interface SessionOption {
  id: string;
  session_name: string | null;
  workflow_phase: string | null;
}

type DataSourceChoice = 'User' | 'Extern';
type TypeExternChoice = 'Wetterprognose' | 'Sonstiges' | '';

interface FeatureState {
  dataSource: DataSourceChoice;
  typeExtern: TypeExternChoice;
  apiSource: string;
  fcstVar: string;
  latitude: string;
  longitude: string;
}

const API_SOURCES = ['GeoSphere', 'OpenWeather', 'Sonstige API'] as const;
type CodeLang = 'curl' | 'python' | 'javascript';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const CopyButton = memo(({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md transition-all duration-150 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 active:scale-95"
    >
      {copied ? <><FaCheck className="text-emerald-400" /> Copied</> : <><FaCopy /> Copy</>}
    </button>
  );
});
CopyButton.displayName = 'CopyButton';

const StatusBadge = memo(({ configured }: { configured: boolean }) => {
  const { t } = useTranslation('forecastApi');
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
      configured
        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
        : 'bg-amber-50 text-amber-700 border border-amber-200'
    }`}>
      {configured ? <><FaCircleCheck className="text-[8px]" /> {t('features.ready')}</> : <><FaCircleExclamation className="text-[8px]" /> {t('features.configure')}</>}
    </span>
  );
});
StatusBadge.displayName = 'StatusBadge';

const CodeBlock = memo(({ code, lang }: { code: string; lang: string }) => (
  <div className="relative group">
    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <CopyButton text={code} />
    </div>
    <pre className="px-4 py-3 bg-[#0d1117] text-gray-200 rounded-lg text-[12px] leading-relaxed font-mono overflow-x-auto scrollbar-thin scrollbar-thumb-gray-700">
      <code className={`language-${lang}`}>
        {code}
      </code>
    </pre>
  </div>
));
CodeBlock.displayName = 'CodeBlock';

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const ForecastApiPage = () => {
  const { t, i18n } = useTranslation('forecastApi');

  // Session selection
  const [sessions, setSessions] = useState<SessionOption[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [loadingSessions, setLoadingSessions] = useState(true);

  // Config
  const [config, setConfig] = useState<SessionConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  // Feature states
  const [featureStates, setFeatureStates] = useState<Record<string, FeatureState>>({});
  const [apiParams, setApiParams] = useState<Record<string, ApiParameter[]>>({});
  const [loadingParams, setLoadingParams] = useState<Record<string, boolean>>({});

  // Timezone
  const [selectedTimezone, setSelectedTimezone] = useState<string>('UTC');

  // Save state
  const [saving, setSaving] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Code example tab
  const [codeLang, setCodeLang] = useState<CodeLang>('curl');

  // API Keys
  const [apiKeys, setApiKeys] = useState<ApiKeyInfo[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState<string>('none');
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState<string | null>(null);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [keyToRevoke, setKeyToRevoke] = useState<{ id: string; name: string } | null>(null);
  const [showApiUpgradeModal, setShowApiUpgradeModal] = useState(false);
  const [apiUpgradeMessage, setApiUpgradeMessage] = useState('');

  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  // Publish current forecast-API settings to the chatbot assistant context
  const chatContext = useMemo(() => {
    const fields: ChatField[] = [];
    fields.push({ label: 'Zeitzone', value: selectedTimezone });
    fields.push({ label: 'Konfigurierte Variablen', value: String(Object.keys(featureStates ?? {}).length) });
    const apiSources = Array.from(
      new Set(
        Object.values(featureStates ?? {})
          .map((f) => f.apiSource)
          .filter(Boolean)
      )
    ).join(', ');
    if (apiSources) fields.push({ label: 'API-Quellen', value: apiSources });
    return { fields };
  }, [selectedTimezone, featureStates]);
  usePublishChatContext(chatContext);

  // -------------------------------------------------------------------------
  // Load sessions list
  // -------------------------------------------------------------------------

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingSessions(true);
        const { supabase } = await import('../../api/client/supabaseClient');
        const { data } = await supabase
          .from('sessions')
          .select('id, session_name, workflow_phase')
          .in('workflow_phase', ['phase4', 'completed'])
          .order('created_at', { ascending: false });
        setSessions(data || []);
      } catch {
        setSessions([]);
      } finally {
        setLoadingSessions(false);
      }
    };
    load();
  }, []);

  // -------------------------------------------------------------------------
  // Load session config
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!selectedSessionId) {
      setConfig(null);
      setFeatureStates({});
      setConfigSaved(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadingConfig(true);
      setConfigError(null);
      setConfigSaved(false);
      try {
        const resp = await TrainingApiService.getForecastConfig(selectedSessionId);
        if (cancelled) return;
        const data = resp as unknown as SessionConfig;
        setConfig(data);

        // Init feature states
        const initial: Record<string, FeatureState> = {};
        for (const f of data.files) {
          if (f.type === 'input') {
            initial[f.bezeichnung] = {
              dataSource: (f.data_source === 'Extern' ? 'Extern' : 'User') as DataSourceChoice,
              typeExtern: f.data_source === 'Extern' ? 'Wetterprognose' : '',
              apiSource: f.api_source ?? '',
              fcstVar: f.fcst_var ?? '',
              latitude: f.latitude != null ? String(f.latitude) : '',
              longitude: f.longitude != null ? String(f.longitude) : '',
            };
          }
        }
        setFeatureStates(initial);

        if (data.time_info?.zeitzone) {
          setSelectedTimezone(String(data.time_info.zeitzone));
        }

        // Check if already configured
        const inputFiles = data.files.filter(f => f.type === 'input');
        const allConfigured = inputFiles.every(f => f.data_source);
        if (allConfigured) setConfigSaved(true);

      } catch (err) {
        if (!cancelled) setConfigError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedSessionId]);

  // -------------------------------------------------------------------------
  // Feature state handlers
  // -------------------------------------------------------------------------

  const updateFeature = useCallback((name: string, patch: Partial<FeatureState>) => {
    setFeatureStates(prev => ({ ...prev, [name]: { ...prev[name], ...patch } }));
    setConfigSaved(false);
  }, []);

  const fetchApiParams = useCallback(async (source: string) => {
    if (apiParams[source] || loadingParams[source]) return;
    setLoadingParams(prev => ({ ...prev, [source]: true }));
    try {
      const resp = await TrainingApiService.getApiParameters(source);
      const data = resp as unknown as { parameters: ApiParameter[] };
      setApiParams(prev => ({ ...prev, [source]: data.parameters || [] }));
    } catch { /* ignore */ }
    finally { setLoadingParams(prev => ({ ...prev, [source]: false })); }
  }, [apiParams, loadingParams]);

  // Prefetch parameters for any apiSource already set when a session is loaded
  // (handleApiSourceChange only runs on user interaction, not on session load).
  useEffect(() => {
    Object.values(featureStates).forEach(state => {
      if (state.apiSource && state.apiSource !== 'Sonstige API') {
        fetchApiParams(state.apiSource);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureStates]);

  const handleSourceChange = useCallback((name: string, source: DataSourceChoice) => {
    updateFeature(name, { dataSource: source, typeExtern: '', apiSource: '', fcstVar: '', latitude: '', longitude: '' });
  }, [updateFeature]);

  const handleTypeExternChange = useCallback((name: string, typeExtern: TypeExternChoice) => {
    updateFeature(name, { typeExtern, apiSource: '', fcstVar: '', latitude: '', longitude: '' });
  }, [updateFeature]);

  const handleApiSourceChange = useCallback((name: string, apiSource: string) => {
    updateFeature(name, { apiSource, fcstVar: '' });
    if (apiSource && apiSource !== 'Sonstige API') fetchApiParams(apiSource);
  }, [updateFeature, fetchApiParams]);

  // -------------------------------------------------------------------------
  // Validation
  // -------------------------------------------------------------------------

  const isValid = useCallback((): boolean => {
    if (!config) return false;
    const inputFiles = config.files.filter(f => f.type === 'input');
    for (const f of inputFiles) {
      const state = featureStates[f.bezeichnung];
      if (!state) return false;
      if (state.dataSource === 'User') continue;
      if (state.dataSource === 'Extern') {
        if (!state.typeExtern) return false;
        if (state.typeExtern === 'Sonstiges') return false;
        if (state.typeExtern === 'Wetterprognose') {
          if (!state.apiSource || state.apiSource === 'Sonstige API') return false;
          if (!state.fcstVar || !state.latitude || !state.longitude) return false;
          if (isNaN(parseFloat(state.latitude)) || isNaN(parseFloat(state.longitude))) return false;
        }
      }
    }
    return true;
  }, [config, featureStates]);

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------

  const handleSave = useCallback(async () => {
    if (!config) return;
    setSaving(true);
    setSaveError(null);
    try {
      const features: SaveForecastConfigPayload['features'] = [];
      for (const f of config.files) {
        if (f.type !== 'input') continue;
        const state = featureStates[f.bezeichnung];
        if (!state) continue;
        features.push({
          id: f.id,
          data_source: state.dataSource,
          api_source: state.dataSource === 'Extern' && state.typeExtern === 'Wetterprognose' && state.apiSource !== 'Sonstige API' ? state.apiSource : undefined,
          fcst_var: state.dataSource === 'Extern' && state.typeExtern === 'Wetterprognose' ? state.fcstVar : undefined,
          latitude: state.latitude ? parseFloat(state.latitude) : undefined,
          longitude: state.longitude ? parseFloat(state.longitude) : undefined,
          feature_index: f.feature_index ?? undefined,
        });
      }
      await TrainingApiService.saveForecastConfig(selectedSessionId, { features, zeitzone: selectedTimezone });
      setConfigSaved(true);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [config, featureStates, selectedSessionId, selectedTimezone]);

  // -------------------------------------------------------------------------
  // API Keys
  // -------------------------------------------------------------------------

  // Load API keys when session selected and config saved
  useEffect(() => {
    if (!selectedSessionId || !configSaved) { setApiKeys([]); return; }
    const loadKeys = async () => {
      setLoadingKeys(true);
      try {
        const resp = await TrainingApiService.listApiKeys(selectedSessionId);
        const data = resp as unknown as { keys: ApiKeyInfo[] };
        setApiKeys(data.keys || []);
      } catch { setApiKeys([]); }
      finally { setLoadingKeys(false); }
    };
    loadKeys();
  }, [selectedSessionId, configSaved]);

  const handleGenerateKey = useCallback(async () => {
    if (!newKeyName.trim() || !selectedSessionId) return;
    setGeneratingKey(true);
    setKeyError(null);
    setNewlyGeneratedKey(null);
    try {
      const expiresInDays = newKeyExpiry === 'none' ? null : parseInt(newKeyExpiry);
      const resp = await TrainingApiService.generateApiKey(selectedSessionId, newKeyName.trim(), expiresInDays);
      const data = resp as unknown as { key: string };
      setNewlyGeneratedKey(data.key);
      setNewKeyName('');
      setNewKeyExpiry('none');
      setShowGenerateForm(false);
      // Reload keys list
      const listResp = await TrainingApiService.listApiKeys(selectedSessionId);
      const listData = listResp as unknown as { keys: ApiKeyInfo[] };
      setApiKeys(listData.keys || []);
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'API_LIMIT_REACHED' || code === 'API_ACCESS_NOT_IN_PLAN') {
        setApiUpgradeMessage(err instanceof Error ? err.message : '');
        setShowApiUpgradeModal(true);
      } else {
        setKeyError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setGeneratingKey(false);
    }
  }, [selectedSessionId, newKeyName, newKeyExpiry]);

  const handleRevokeKey = useCallback((keyId: string, keyName: string) => {
    setKeyToRevoke({ id: keyId, name: keyName });
  }, []);

  const confirmRevoke = useCallback(async () => {
    if (!keyToRevoke) return;
    const { id } = keyToRevoke;
    setKeyToRevoke(null);
    try {
      await TrainingApiService.revokeApiKey(id);
      const resp = await TrainingApiService.listApiKeys(selectedSessionId);
      const data = resp as unknown as { keys: ApiKeyInfo[] };
      setApiKeys(data.keys || []);
    } catch (err) {
      setKeyError(err instanceof Error ? err.message : String(err));
    }
  }, [keyToRevoke, selectedSessionId]);

  // -------------------------------------------------------------------------
  // Code examples
  // -------------------------------------------------------------------------

  const userFeatureNames = useMemo(() => {
    if (!config) return [];
    return config.files
      .filter(f => f.type === 'input' && featureStates[f.bezeichnung]?.dataSource === 'User')
      .map(f => f.bezeichnung);
  }, [config, featureStates]);

  const outputFeatureNames = useMemo(() => {
    if (!config) return [];
    return config.files.filter(f => f.type === 'output').map(f => f.bezeichnung);
  }, [config]);

  const endpointUrl = `${apiBaseUrl}/forecast/${selectedSessionId}`;

  const codeExamples = useMemo((): Record<CodeLang, string> => {
    // Use generated key, or first active key prefix, or placeholder
    const activeKey = apiKeys.find(k => !k.revoked_at);
    const authToken = newlyGeneratedKey || (activeKey ? `${activeKey.key_prefix}...` : 'YOUR_API_KEY');

    const userDataObj: Record<string, string> = {};
    for (const name of userFeatureNames) {
      userDataObj[name] = `{"UTC": ["2026-03-31T08:00:00", ...], "${name}": [1234.56, ...]}`;
    }

    const curlBody = JSON.stringify({
      user_data: Object.fromEntries(
        userFeatureNames.map(n => [n, { UTC: ['...'], [n]: [0] }])
      )
    });

    return {
      curl: `curl -X POST "${endpointUrl}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${authToken}" \\
  -d '${curlBody}'`,

      python: `import requests

url = "${endpointUrl}"
headers = {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${authToken}"
}

# Your fresh measurement data
user_data = {
${userFeatureNames.map(n => `    "${n}": {
        "UTC": ["2026-03-31T08:00:00", "2026-03-31T08:15:00", ...],
        "${n}": [1234.56, 1245.78, ...]
    }`).join(',\n')}
}

response = requests.post(url, json={"user_data": user_data}, headers=headers)
forecast = response.json()

# forecast["forecasts"]["${outputFeatureNames[0] || 'output'}"]
# => {"UTC": ["...", ...], "${outputFeatureNames[0] || 'output'}": [2345.67, ...]}`,

      javascript: `const response = await fetch("${endpointUrl}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${authToken}"
  },
  body: JSON.stringify({
    user_data: {
${userFeatureNames.map(n => `      "${n}": {
        UTC: ["2026-03-31T08:00:00", "2026-03-31T08:15:00", ...],
        "${n}": [1234.56, 1245.78, ...]
      }`).join(',\n')}
    }
  })
});

const forecast = await response.json();
// forecast.forecasts.${outputFeatureNames[0] || 'output'}
// => { UTC: ["...", ...], "${outputFeatureNames[0] || 'output'}": [2345.67, ...] }`
    };
  }, [endpointUrl, userFeatureNames, outputFeatureNames, apiKeys, newlyGeneratedKey]);

  // -------------------------------------------------------------------------
  // Computed
  // -------------------------------------------------------------------------

  const inputFiles = config?.files.filter(f => f.type === 'input') ?? [];
  const outputFiles = config?.files.filter(f => f.type === 'output') ?? [];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 pb-12" role="main" aria-label={t('title')}>

      {/* ── Page Header ── */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <FaTerminal className="text-primary-foreground text-sm" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">
            {t('title')}
          </h1>
        </div>
        <p className="text-sm text-muted-foreground ml-11">
          {t('subtitle')}
        </p>
      </div>

      <div role="list">

      {/* ── Step 1: Select Session ── */}
      <section role="listitem" aria-labelledby="step-1-heading" className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">1</span>
          <h2 id="step-1-heading" className="text-sm font-semibold text-foreground uppercase tracking-wide">{t('steps.selectSession')}</h2>
          <InfoTooltip text={t('tooltips.sessionSection')} width="w-80" align="start" />
        </div>
        <div className="rounded-xl border border-border bg-card shadow-sm p-4">
          {loadingSessions ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><FaSpinner className="animate-spin" /> {t('session.loading')}</div>
          ) : sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('session.noSessions')}</p>
          ) : (
            <>
              <label htmlFor="session-selector" className="sr-only">{t('steps.selectSession')}</label>
              <select
                id="session-selector"
                value={selectedSessionId}
                onChange={e => setSelectedSessionId(e.target.value)}
                className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm text-foreground focus:border-primary focus:ring-2 focus:ring-ring/20 outline-none transition-all"
              >
              <option value="">{t('session.selectPlaceholder')}</option>
              {sessions.map(s => (
                <option key={s.id} value={s.id}>
                  {s.session_name || s.id.slice(0, 8)}{s.workflow_phase ? ` (${s.workflow_phase})` : ''}
                </option>
              ))}
            </select>
            </>
          )}
        </div>
      </section>

      {/* ── Step 2: Configure Features ── */}
      {config && !loadingConfig && (
        <section role="listitem" aria-labelledby="step-2-heading" className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">2</span>
            <h2 id="step-2-heading" className="text-sm font-semibold text-foreground uppercase tracking-wide">{t('steps.configureDataSources')}</h2>
            <InfoTooltip text={t('tooltips.dataSourcesSection')} width="w-80" align="start" />
          </div>

          <div className="rounded-xl border border-border bg-card shadow-sm divide-y divide-border/60">

            {/* Input features */}
            {inputFiles.map(file => {
              const state = featureStates[file.bezeichnung];
              if (!state) return null;
              const paramsForSource = state.apiSource ? (apiParams[state.apiSource] ?? []) : [];
              const isLoadingP = state.apiSource ? !!loadingParams[state.apiSource] : false;
              const isConfigured = state.dataSource === 'User' ||
                (state.dataSource === 'Extern' && state.typeExtern === 'Wetterprognose' && state.apiSource && state.apiSource !== 'Sonstige API' && state.fcstVar && state.latitude && state.longitude);

              const baseId = `feature-${file.id}`;
              return (
                <div key={file.bezeichnung} className="px-4 py-4">
                  {/* Feature header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-foreground">{file.bezeichnung}</span>
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {file.zeithorizont_start}h &rarr; {file.zeithorizont_end > 0 ? '+' : ''}{file.zeithorizont_end}h
                      </span>
                    </div>
                    <StatusBadge configured={!!isConfigured} />
                  </div>

                  {/* Source + Type Extern row */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-2.5">
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        <label htmlFor={`${baseId}-source`} className="block text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t('features.source')}</label>
                        <InfoTooltip text={t('tooltips.source')} width="w-64" align="start" />
                      </div>
                      <select id={`${baseId}-source`} value={state.dataSource} onChange={e => handleSourceChange(file.bezeichnung, e.target.value as DataSourceChoice)}
                        className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:ring-1 focus:ring-ring outline-none transition-colors">
                        <option value="User">{t('features.user')}</option>
                        <option value="Extern">{t('features.extern')}</option>
                      </select>
                    </div>

                    {state.dataSource === 'Extern' && (
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <label htmlFor={`${baseId}-type`} className="block text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t('features.type')}</label>
                          <InfoTooltip text={t('tooltips.type')} width="w-64" align="start" />
                        </div>
                        <select id={`${baseId}-type`} value={state.typeExtern} onChange={e => handleTypeExternChange(file.bezeichnung, e.target.value as TypeExternChoice)}
                          className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:ring-1 focus:ring-ring outline-none transition-colors">
                          <option value="">{t('features.selectType')}</option>
                          <option value="Wetterprognose">{t('features.wetterprognose')}</option>
                          <option value="Sonstiges">{t('features.sonstiges')}</option>
                        </select>
                      </div>
                    )}

                    {state.dataSource === 'User' && (
                      <div className="flex items-end pb-2">
                        <span className="text-xs text-muted-foreground italic">{t('features.dataFromStorage')}</span>
                      </div>
                    )}
                  </div>

                  {/* Sonstiges message */}
                  {state.dataSource === 'Extern' && state.typeExtern === 'Sonstiges' && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-2.5 text-sm text-amber-800">
                      {t('features.contactMessage')} <a href="mailto:info@forecast-engine.com" className="font-medium underline">info@forecast-engine.com</a> {t('features.contactUs')}
                    </div>
                  )}

                  {/* Wetterprognose: API config */}
                  {state.dataSource === 'Extern' && state.typeExtern === 'Wetterprognose' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <label htmlFor={`${baseId}-apiSource`} className="block text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t('features.apiSource')}</label>
                          <InfoTooltip text={t('tooltips.apiSource')} width="w-64" align="start" />
                        </div>
                        <select id={`${baseId}-apiSource`} value={state.apiSource} onChange={e => handleApiSourceChange(file.bezeichnung, e.target.value)}
                          className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:ring-1 focus:ring-ring outline-none transition-colors">
                          <option value="">{t('features.selectApi')}</option>
                          {API_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>

                      {state.apiSource === 'Sonstige API' && (
                        <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-2.5 text-sm text-amber-800">
                          {t('features.contactMessage')} <a href="mailto:info@forecast-engine.com" className="font-medium underline">info@forecast-engine.com</a> {t('features.contactUs')}
                        </div>
                      )}

                      {state.apiSource && state.apiSource !== 'Sonstige API' && (
                        <>
                          <div>
                            <div className="flex items-center gap-1 mb-1">
                              <label htmlFor={`${baseId}-parameter`} className="block text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t('features.parameter')}</label>
                              <InfoTooltip text={t('tooltips.parameter')} width="w-64" align="start" />
                            </div>
                            {isLoadingP ? (
                              <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"><FaSpinner className="animate-spin" /> Loading...</div>
                            ) : (
                              <select id={`${baseId}-parameter`} value={state.fcstVar} onChange={e => updateFeature(file.bezeichnung, { fcstVar: e.target.value })}
                                className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:ring-1 focus:ring-ring outline-none transition-colors">
                                <option value="">{t('features.selectParameter')}</option>
                                {paramsForSource.map(p => <option key={p.parameter} value={p.name_user}>{p.name_user}</option>)}
                              </select>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-1 mb-1">
                              <label htmlFor={`${baseId}-latitude`} className="block text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t('features.latitude')}</label>
                              <InfoTooltip text={t('tooltips.latitude')} width="w-64" align="end" />
                            </div>
                            <input id={`${baseId}-latitude`} type="text" inputMode="decimal" value={state.latitude} placeholder="47.0707"
                              onChange={e => updateFeature(file.bezeichnung, { latitude: e.target.value })}
                              className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:ring-1 focus:ring-ring outline-none transition-colors placeholder:text-muted-foreground" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1 mb-1">
                              <label htmlFor={`${baseId}-longitude`} className="block text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t('features.longitude')}</label>
                              <InfoTooltip text={t('tooltips.longitude')} width="w-64" align="end" />
                            </div>
                            <input id={`${baseId}-longitude`} type="text" inputMode="decimal" value={state.longitude} placeholder="15.4395"
                              onChange={e => updateFeature(file.bezeichnung, { longitude: e.target.value })}
                              className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:ring-1 focus:ring-ring outline-none transition-colors placeholder:text-muted-foreground" />
                          </div>
                          {state.apiSource === 'GeoSphere' && (
                            <div className="sm:col-span-2 rounded-lg bg-accent/60 border border-primary/20 px-3 py-2 text-[11px] text-accent-foreground space-y-0.5">
                              <div><strong>GeoSphere Austria</strong> — {t('geoSphere.coverage')}</div>
                              <div>{t('geoSphere.details')}</div>
                            </div>
                          )}
                          {state.apiSource === 'OpenWeather' && (
                            <div className="sm:col-span-2 rounded-lg bg-orange-50/60 border border-orange-100 px-3 py-2 text-[11px] text-orange-700 space-y-0.5">
                              <div><strong>OpenWeather</strong> — {t('openWeather.coverage')}</div>
                              <div>{t('openWeather.details')}</div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Output features (read-only) */}
            {outputFiles.length > 0 && (
              <div className="px-4 py-3 bg-muted/30 rounded-b-xl">
                <div className="flex items-center gap-1 mb-1.5">
                  <span className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t('features.output')}</span>
                  <InfoTooltip text={t('tooltips.output')} width="w-64" align="start" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {outputFiles.map(f => (
                    <span key={f.bezeichnung} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent border border-primary/20 text-xs font-mono text-accent-foreground">
                      {f.bezeichnung}
                      <span className="text-primary font-normal">{f.zeithorizont_start}h&rarr;+{f.zeithorizont_end}h</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Save button */}
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving || !isValid()}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed active:scale-[0.98] transition-all duration-150 shadow-sm"
            >
              {saving ? <><FaSpinner className="animate-spin" /> {t('saveConfig.saving')}</> : <><FaGear /> {t('saveConfig.button')}</>}
            </button>
            {justSaved && (
              <span className="flex items-center gap-1.5 text-sm text-emerald-600 font-medium transition-opacity">
                <FaCircleCheck /> {t('saveConfig.saved')}
              </span>
            )}
            {saveError && (
              <span className="text-sm text-red-600">{saveError}</span>
            )}
          </div>
        </section>
      )}

      {/* ── Step 3: API Keys ── */}
      {configSaved && config && (
        <section role="listitem" aria-labelledby="step-3-heading" className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">3</span>
            <h2 id="step-3-heading" className="text-sm font-semibold text-foreground uppercase tracking-wide">{t('steps.apiKeys')}</h2>
            <InfoTooltip text={t('tooltips.apiKeysSection')} width="w-80" align="start" />
          </div>

          <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-muted/50 border-b border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{t('apiKeys.description')}</span>
              <button
                onClick={() => { setShowGenerateForm(true); setNewlyGeneratedKey(null); setKeyError(null); }}
                disabled={apiKeys.filter(k => !k.revoked_at).length >= 5}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
              >
                {t('apiKeys.generateButton')}
              </button>
            </div>

            {/* Generate form */}
            {showGenerateForm && (
              <div className="px-4 py-3 border-b border-border bg-accent/30">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[150px]">
                    <div className="flex items-center gap-1 mb-1">
                      <label htmlFor="api-key-name" className="block text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t('apiKeys.name')}</label>
                      <InfoTooltip text={t('tooltips.name')} width="w-64" align="start" />
                    </div>
                    <input
                      id="api-key-name"
                      type="text"
                      value={newKeyName}
                      onChange={e => setNewKeyName(e.target.value)}
                      placeholder={t('apiKeys.namePlaceholder')}
                      className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:ring-1 focus:ring-ring outline-none"
                    />
                  </div>
                  <div className="w-40">
                    <div className="flex items-center gap-1 mb-1">
                      <label htmlFor="api-key-expiration" className="block text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t('apiKeys.expiration')}</label>
                      <InfoTooltip text={t('tooltips.expiration')} width="w-64" align="end" />
                    </div>
                    <select
                      id="api-key-expiration"
                      value={newKeyExpiry}
                      onChange={e => setNewKeyExpiry(e.target.value)}
                      className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:ring-1 focus:ring-ring outline-none"
                    >
                      <option value="none">{t('apiKeys.noExpiry')}</option>
                      <option value="30">{t('apiKeys.days30')}</option>
                      <option value="90">{t('apiKeys.days90')}</option>
                      <option value="365">{t('apiKeys.days365')}</option>
                    </select>
                  </div>
                  <button
                    onClick={handleGenerateKey}
                    disabled={generatingKey || !newKeyName.trim()}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
                  >
                    {generatingKey ? <><FaSpinner className="animate-spin" /> {t('apiKeys.generating')}</> : t('apiKeys.generate')}
                  </button>
                  <button
                    onClick={() => setShowGenerateForm(false)}
                    className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
                  >
                    {t('apiKeys.cancel')}
                  </button>
                </div>
                {keyError && <p className="mt-2 text-sm text-red-600">{keyError}</p>}
              </div>
            )}

            {/* Newly generated key (shown once) */}
            {newlyGeneratedKey && (
              <div className="px-4 py-3 border-b border-border bg-emerald-50">
                <div className="flex items-center gap-2 mb-1.5">
                  <FaCheck className="text-emerald-600 text-xs" />
                  <span className="text-sm font-medium text-emerald-800">{t('apiKeys.keyGenerated')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-[#0d1117] text-emerald-400 rounded-lg text-sm font-mono select-all overflow-x-auto">
                    {newlyGeneratedKey}
                  </code>
                  <CopyButton text={newlyGeneratedKey} />
                </div>
                <p className="mt-1.5 text-[11px] text-amber-700">{t('apiKeys.copyWarning')}</p>
              </div>
            )}

            {/* Keys list */}
            <div className="divide-y divide-border/60">
              {loadingKeys ? (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground"><FaSpinner className="inline animate-spin mr-2" />{t('apiKeys.loadingKeys')}</div>
              ) : apiKeys.filter(k => !k.revoked_at).length === 0 && apiKeys.filter(k => k.revoked_at).length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">{t('apiKeys.noKeys')}</div>
              ) : (
                <>
                  {/* Active keys */}
                  {apiKeys.filter(k => !k.revoked_at).map(k => (
                    <div key={k.id} className="px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <code className="text-xs font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">{k.key_prefix}...</code>
                        <span className="text-sm font-medium text-foreground">{k.name}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {k.expires_at ? `${t('apiKeys.expires')} ${new Date(k.expires_at).toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US')}` : t('apiKeys.noExpiry')}
                        </span>
                        {k.last_used_at && (
                          <span className="text-[11px] text-muted-foreground">{t('apiKeys.lastUsed')} {new Date(k.last_used_at).toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US')}</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleRevokeKey(k.id, k.name)}
                        className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
                      >
                        {t('apiKeys.revoke')}
                      </button>
                    </div>
                  ))}

                  {/* Revoked keys */}
                  {apiKeys.filter(k => k.revoked_at).length > 0 && (
                    <div className="px-4 py-2 bg-muted/30">
                      <span className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t('apiKeys.revoked')}</span>
                      {apiKeys.filter(k => k.revoked_at).map(k => (
                        <div key={k.id} className="flex items-center gap-3 py-1 opacity-50">
                          <code className="text-xs font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">{k.key_prefix}...</code>
                          <span className="text-xs text-muted-foreground line-through">{k.name}</span>
                          <span className="text-[10px] text-muted-foreground">{t('apiKeys.revoked')} {new Date(k.revoked_at!).toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Step 4: API Endpoint ── */}
      {configSaved && config && (
        <section role="listitem" aria-labelledby="step-4-heading" className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center">4</span>
            <h2 id="step-4-heading" className="text-sm font-semibold text-foreground uppercase tracking-wide">{t('steps.yourApiEndpoint')}</h2>
            <InfoTooltip text={t('tooltips.apiEndpointSection')} width="w-80" align="start" />
          </div>

          <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">

            {/* Endpoint URL bar */}
            <div className="px-4 py-3 bg-[#0d1117] flex items-center gap-3">
              <span className="shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">POST</span>
              <code className="flex-1 text-sm font-mono text-gray-200 overflow-x-auto whitespace-nowrap select-all scrollbar-thin scrollbar-thumb-gray-700">{endpointUrl}</code>
              <CopyButton text={endpointUrl} />
            </div>

            {/* Auth note */}
            <div className="px-4 py-2.5 bg-muted border-b border-border text-xs text-muted-foreground">
              {t('endpoint.auth')} <code className="px-1 py-0.5 rounded bg-muted text-foreground text-[11px]">Authorization: Bearer sk_fcst_...</code> {t('endpoint.authHeader')}
            </div>

            {/* Code examples with tabs */}
            <div className="px-4 pt-3 pb-4">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t('endpoint.requestExample')}</span>
                <div className="flex rounded-lg border border-border overflow-hidden">
                  {(['curl', 'python', 'javascript'] as CodeLang[]).map(lang => (
                    <button
                      key={lang}
                      onClick={() => setCodeLang(lang)}
                      className={`px-3 py-1 text-[11px] font-medium transition-colors ${
                        codeLang === lang
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-card text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {lang === 'curl' ? 'cURL' : lang === 'python' ? 'Python' : 'JavaScript'}
                    </button>
                  ))}
                </div>
              </div>
              <CodeBlock code={codeExamples[codeLang]} lang={codeLang} />
            </div>

            {/* Response example */}
            <div className="px-4 pb-4">
              <span className="block text-[10px] uppercase tracking-wide font-medium text-muted-foreground mb-2.5">{t('endpoint.response')}</span>
              <CodeBlock code={JSON.stringify({
                success: true,
                forecasts: Object.fromEntries(
                  outputFeatureNames.map(n => [n, {
                    UTC: ['2026-03-31T08:00:00', '2026-03-31T08:15:00', '...'],
                    [n]: [2345.67, 2401.23, '...']
                  }])
                ),
                utc_ref: '2026-03-31T08:00:00',
                model_used: config.models?.[0]?.filename ?? 'best_model.h5',
                n_predictions: config.zeitschritte?.ausgabe ?? 49
              }, null, 2)} lang="json" />
            </div>

            {/* How it works */}
            <div className="px-4 py-3 bg-muted/40 border-t border-border">
              <div className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground mb-2">{t('endpoint.howItWorks')}</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { step: '1', title: t('endpoint.youSend'), desc: `${t('endpoint.freshDataFor')} ${userFeatureNames.join(', ') || 'User features'}` },
                  { step: '2', title: t('endpoint.serverFetches'), desc: `${t('endpoint.liveWeatherFrom')} ${inputFiles.filter(f => featureStates[f.bezeichnung]?.dataSource === 'Extern').map(f => featureStates[f.bezeichnung]?.apiSource).filter(Boolean).join(', ') || 'configured APIs'}` },
                  { step: '3', title: t('endpoint.modelReturns'), desc: `${t('endpoint.predictionsFor')} ${outputFeatureNames.join(', ') || 'output features'}` },
                ].map(item => (
                  <div key={item.step} className="flex items-start gap-2">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-accent text-primary text-[10px] font-bold flex items-center justify-center mt-0.5">{item.step}</span>
                    <div>
                      <div className="text-xs font-semibold text-foreground">{item.title}</div>
                      <div className="text-[11px] text-muted-foreground">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      </div>{/* end role="list" */}

      {/* ── Loading / Error ── */}
      {loadingConfig && (
        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
          <FaSpinner className="animate-spin" /> {t('loading')}
        </div>
      )}
      {configError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-6">
          <p className="text-sm text-red-700">{configError}</p>
        </div>
      )}

      <ConfirmDialog
        isOpen={keyToRevoke !== null}
        title={t('apiKeys.revokeTitle', { defaultValue: t('apiKeys.revoke') })}
        message={t('apiKeys.revokeConfirm', { name: keyToRevoke?.name ?? '' })}
        destructive={true}
        onConfirm={confirmRevoke}
        onCancel={() => setKeyToRevoke(null)}
      />

      <UpgradeModal
        isOpen={showApiUpgradeModal}
        onClose={() => setShowApiUpgradeModal(false)}
        resourceType="api"
        message={apiUpgradeMessage}
      />

    </div>
  );
};

export default ForecastApiPage;
